//#region Import
import { useMemo, useState } from "react";
import { useTranslation } from "@1f/react-sdk";

//Vapor / MUI Components
import VaporTag from "@vapor/react-custom/VaporTag";
import Stack from "@vapor/react-material/Stack";
import Button from "@vapor/react-material/Button";
import Tooltip from "@vapor/react-material/Tooltip";
import { DataGridPro, GridToolbar, itIT } from "@mui/x-data-grid-pro";

//Icons
import { Search as CervedIcon } from "@mui/icons-material";

//Custom styles
import { chipblue, chipYellow, chipGreen } from "../../../businessLogic/styles";

//Custom components
import { NoData } from "../../../components/NoData";
import { getExistingRowFascicolo } from "../../../businessLogic/helpers/fascicoloSearchHelpers";
//#endregion

// Griglia unica dei risultati di ricerca (PARS-1402): righe normalizzate da Archivio
// (db), Workspace (wks) ed Ex Cerved (cerved) con badge di provenienza in prima colonna.
export const FascicoloSearchResultsGrid = ({
  results = [],
  onRowSelect,
  onExtendToCerved,
  handleResetForm,
  cervedSearched = false,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [selectionModel, setSelectionModel] = useState([]);

  // Riga attualmente selezionata: i suoi dati vengono usati dal pulsante "Crea fascicolo".
  const selectedRow = useMemo(
    () => results.find((row) => row.rowId === selectionModel[0]) || null,
    [results, selectionModel]
  );

  // D blu (DB interno), W giallo (Workspace), C verde (Cerved).
  const sourceMeta = useMemo(
    () => ({
      db: {
        label: "D",
        tooltip: t("fascicolo.new.search.source.db", "DB interno"),
        sx: chipblue,
      },
      wks: {
        label: "W",
        tooltip: t("fascicolo.new.search.source.wks", "Workspace"),
        sx: chipYellow,
      },
      ModeFinance: {
        label: "M",
        tooltip: t("fascicolo.new.search.source.modefinance", "ModeFinance"),
        sx: chipGreen,
      },
    }),
    [t]
  );

  const columns = useMemo(
    () => [
      {
        field: "source",
        headerName: t("fascicolo.new.search.col.source", "Fonte"),
        flex: 0.06,
        minWidth: 70,
        filterable: true,
        sortable: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const meta = sourceMeta[params.value];
          if (!meta) return null;
          return (
            <Tooltip title={meta.tooltip} arrow>
              <span>
                <VaporTag variant="standard" label={meta.label} sx={meta.sx} />
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: "denominazione",
        headerName: t("fascicolo.new.search.col.denominazione", "Denominazione / Nominativo"),
        flex: 0.28,
        filterable: true,
      },
      {
        field: "codiceFiscale",
        headerName: t("fascicolo.new.search.col.cf", "Codice Fiscale"),
        flex: 0.16,
        filterable: true,
      },
      {
        field: "partitaIva",
        headerName: t("fascicolo.new.search.col.piva", "P. IVA"),
        flex: 0.14,
        filterable: true,
      },
      {
        field: "sede",
        headerName: t("fascicolo.new.search.col.sede", "Sede"),
        flex: 0.2,
        filterable: true,
      },
      {
        field: "stato",
        headerName: t("fascicolo.new.search.col.stato", "Stato"),
        flex: 0.12,
        filterable: true,
      },
    ],
    [t, sourceMeta]
  );

  const showExtendButton = typeof onExtendToCerved === "function" && !cervedSearched;

  const handleCreateClick = () => {
    if (selectedRow) onRowSelect && onRowSelect(selectedRow);
  };

  return (
    <Stack spacing={2}>
      <Stack spacing={2} direction="row" justifyContent="right" alignItems="center">
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedRow}
          onClick={handleCreateClick}
        >
          {getExistingRowFascicolo(selectedRow)
            ? t("fascicolo.fascicoloNew.goToFolder", "Vai al fascicolo")
            : t("fascicolo.new.search.createFromSelection", "Crea fascicolo")}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleResetForm}
        >
          {t("fascicolo.fascicoloNew.newSearch")}
        </Button>
        {showExtendButton && (
          <Button
            variant="outlined"            
            startIcon={<CervedIcon />}
            onClick={onExtendToCerved}
          >
            {t("fascicolo.new.search.extendToCerved", "Estendi a ModeFinance")}
          </Button>
        )}
      </Stack>
      {results && results.length > 0 ? (
        <DataGridPro
          rows={results}
          columns={columns}
          getRowId={(row) => row.rowId}
          autoHeight
          loading={loading}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { printOptions: { disableToolbarButton: true } } }}
          localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
          pagination
          pageSizeOptions={[10, 25, 50]}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(model) => setSelectionModel(model)}
          onRowClick={(params) => setSelectionModel([params.id])}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
        />
      ) : (
        <NoData size="small" />
      )}
    </Stack>
  );
};
